import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Stack,
  Text,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { addYears, parseISO } from 'date-fns';

import {
  CloseCircleOutline as CloseCircle,
  CheckOutline,
  CustomAlert,
  DatePickerInput,
  useToast,
} from '@aslanpay/portal-common-lib';

import useFetchIndividual from 'app/hooks/useFetchIndividual';
import useFetchPayCalendar from 'app/hooks/useFetchPayCalendar';
import useTerminateIndividual from 'app/hooks/useTerminateIndividual';

import { terminateEmployeeSchema } from 'app/utils/schemas';

import { TerminateEmployeeFormFields, TerminateEmployeeModalProps } from './types';

function TerminateEmployeeModal({ id, isOpen, onClose, onTerminate }: TerminateEmployeeModalProps) {
  const toast = useToast();

  // Fetch individual to get their pay calendar ID
  const { data: individual } = useFetchIndividual(id);

  // Fetch pay calendar to get pay cycles
  const { data: payCalendar } = useFetchPayCalendar(individual?.payCalendarId);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<TerminateEmployeeFormFields>({
    mode: 'onChange',
    defaultValues: { terminationDate: '' },
    resolver: yupResolver(terminateEmployeeSchema),
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const { mutate: terminateEmployee, isLoading: isTerminatingEmployee } = useTerminateIndividual({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Leave date set',
        description: 'The employee will be marked as left in the system after this date',
      });

      onTerminate();
      handleClose();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, the employee leave date was not set. Please try again.',
      });
    },
  });

  const handleTerminateEmployee = (data: TerminateEmployeeFormFields) => {
    terminateEmployee({
      id,
      terminationDate: data.terminationDate,
    });
  };

  // Calculate date restrictions - use the last generated pay cycle's cutoff date
  const today = new Date();
  let maxDate = addYears(today, 1); // Fallback to 1 year

  // Find the pay cycle with the latest start date and use its cutoff date
  if (payCalendar?.payCycles && payCalendar.payCycles.length > 0) {
    // Sort by start date descending to get the latest pay cycle
    const latestPayCycle = payCalendar.payCycles
      .filter((cycle) => cycle.startDate && cycle.cutoffDate)
      .sort((a, b) => {
        const dateA = parseISO(a.startDate as string);
        const dateB = parseISO(b.startDate as string);
        return dateB.getTime() - dateA.getTime();
      })[0];

    if (latestPayCycle?.cutoffDate) {
      maxDate = parseISO(latestPayCycle.cutoffDate as string);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Text as="h6">Set leave date</Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircle boxSize="30px" />}
              aria-label="Close set leave date"
              onClick={handleClose}
            />
          </HStack>
        </ModalHeader>
        <ModalBody mt="20px">
          <form
            id="terminate-employee-form"
            onSubmit={handleSubmit(handleTerminateEmployee)}
            noValidate
          >
            <Stack spacing="20px">
              <CustomAlert
                variant="simple"
                status="info"
                title={
                  <>
                    <Text textStyle="extraSmallMedium">What is the leave date?</Text>
                    <Text textStyle="extraSmallRegular">
                      This is to mark that the employee has left employment and can no longer accrue
                      a salary via Aslan. The employee can access all services until 23:59:59 on the
                      designated leave date. Upon the leave date passing, the employees card is
                      automatically cancelled.
                    </Text>
                  </>
                }
              />
              <FormControl isInvalid={!!errors?.terminationDate?.message} isRequired>
                <FormLabel>Leave date</FormLabel>
                <Controller
                  control={control}
                  name="terminationDate"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <DatePickerInput
                      placeholder="Set a date"
                      defaultValue={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      maxDate={maxDate}
                    />
                  )}
                />
                <FormErrorMessage>{errors?.terminationDate?.message}</FormErrorMessage>
              </FormControl>
            </Stack>
          </form>
        </ModalBody>
        <ModalFooter>
          <Text color="system.error" textStyle="extraSmallMedium">
            * mandatory
          </Text>
          <Spacer />
          <Button variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            form="terminate-employee-form"
            type="submit"
            size="sm"
            rightIcon={<CheckOutline boxSize="20px" />}
            isLoading={isTerminatingEmployee}
            isDisabled={!isValid}
          >
            Set
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default TerminateEmployeeModal;
